import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { SxProps, Theme } from '@mui/material/styles';

/** Card chrome shared by the header, sections and metadata footer. */
export const detailCardSx: SxProps<Theme> = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '12px',
  bgcolor: 'background.paper',
  minWidth: 0,
};

/** Placeholder for a missing value - the only one detail views use. */
export const EmptyValue = () => (
  <Typography variant="subtitle1" component="span" sx={{ color: 'text.disabled' }}>
    —
  </Typography>
);

/** The plain text style used for a row's value. */
export const DetailValue = ({ children, multiline = false }: { children: React.ReactNode; multiline?: boolean }) => (
  <Typography
    variant="subtitle1"
    component="div"
    sx={{ wordBreak: 'break-word', minWidth: 0, whiteSpace: multiline ? 'pre-wrap' : undefined }}
  >
    {children}
  </Typography>
);

const isEmpty = (value: React.ReactNode) =>
  value === null || value === undefined || value === false || value === '';

interface DetailRowProps {
  label: React.ReactNode;
  /** Strings and numbers are styled as {@link DetailValue}; empty values show {@link EmptyValue}. */
  value?: React.ReactNode;
  /** Alternative to `value` for longer JSX. */
  children?: React.ReactNode;
  /** Keeps line breaks of long free text and top-aligns it. */
  multiline?: boolean;
  /** Top-align the value, for rows holding multi-line content. */
  alignTop?: boolean;
}

/** A single "label / value" line inside a {@link DetailSection}. */
export const DetailRow = ({ label, value: valueProp, children, multiline = false, alignTop = false }: DetailRowProps) => {
  const value = valueProp ?? children;
  let content: React.ReactNode = value;
  if (isEmpty(value)) {
    content = <EmptyValue />;
  } else if (typeof value === 'string' || typeof value === 'number') {
    content = <DetailValue multiline={multiline}>{value}</DetailValue>;
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'minmax(140px, 220px) minmax(0, 1fr)' },
        gap: { xs: 0.25, sm: 2 },
        alignItems: 'start',
        py: 1.25,
        px: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Typography variant="body2" component="div" sx={{ color: 'text.secondary', fontWeight: 600, pt: { sm: 0.25 } }}>
        {label}
      </Typography>
      <Box
        sx={{
          minHeight: '24px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
          alignItems: alignTop || multiline ? 'flex-start' : 'center',
          minWidth: 0,
        }}
      >
        {content}
      </Box>
    </Box>
  );
};

interface DetailSectionProps {
  title?: React.ReactNode;
  icon?: React.ReactNode;
  description?: React.ReactNode;
  /** Rendered on the section header line, e.g. an edit button or a link. */
  action?: React.ReactNode;
  /** Free content (tables, chips, accordions) instead of a list of rows. */
  padded?: boolean;
  /** Span the whole {@link DetailGrid} row. */
  fullWidth?: boolean;
  /** Let the header toggle the body open and closed. */
  collapsible?: boolean;
  /** Initial state of a collapsible section. */
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

/** A titled card; its body is a list of {@link DetailRow}s unless `padded`. */
export const DetailSection = ({
  title,
  icon,
  description,
  action,
  padded = false,
  fullWidth = false,
  collapsible = false,
  defaultExpanded = true,
  children,
}: DetailSectionProps) => {
  const [expanded, setExpanded] = useState(!collapsible || defaultExpanded);
  const toggle = () => setExpanded((previous) => !previous);

  return (
    <Box
      component="section"
      sx={{ ...detailCardSx, overflow: 'hidden', gridColumn: fullWidth ? '1 / -1' : undefined }}
    >
      {(title || action || description) && (
        <Box
          {...(collapsible && {
            role: 'button',
            tabIndex: 0,
            'aria-expanded': expanded,
            onClick: toggle,
            onKeyDown: (event: React.KeyboardEvent) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                toggle();
              }
            },
          })}
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: expanded ? '1px solid' : 'none',
            borderColor: 'divider',
            cursor: collapsible ? 'pointer' : undefined,
            '&:hover': collapsible ? { bgcolor: 'action.hover' } : undefined,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
              {icon}
              {title && (
                <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {title}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
              {/* Clicks on the action (e.g. a link) must not toggle the section. */}
              {action && <Box onClick={(event) => event.stopPropagation()}>{action}</Box>}
              {collapsible && (
                <ExpandMoreIcon
                  sx={{
                    color: 'text.secondary',
                    transition: 'transform 0.2s',
                    transform: expanded ? 'rotate(180deg)' : 'none',
                  }}
                />
              )}
            </Box>
          </Box>
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          )}
        </Box>
      )}
      <Collapse in={expanded} unmountOnExit={false}>
        <Box sx={padded ? { p: 2 } : undefined}>{children}</Box>
      </Collapse>
    </Box>
  );
};

/** A hover hint next to a row label, for rules that need a sentence to explain. */
export const InfoHint = ({ text }: { text: string }) => (
  <Tooltip title={text}>
    <InfoOutlinedIcon
      fontSize="small"
      sx={{ color: 'text.secondary', cursor: 'help', ml: 0.5, verticalAlign: 'middle' }}
    />
  </Tooltip>
);
